<template>
	<div class="overflow-y-hidden">
		<ProgressLinear v-if="loading"/>
		<MediaPlanHeader
			v-if="!loading && !is_show_booking"
			id="media-plan-header"
			class="mb-2"
			:color="media_color"
			:privileges="privileges"
			:groupBy="group_by" 
			:groupByItems="group_by_items"
			:status="status"
			:statusItems="status_items"
			:dateRange="date_range"
			:dateRangeItems="date_range_items"
			:viewing="viewing"
			:viewingItems="viewing_items"
			:columnHeader="column_header"
			:snapshots="snapshots"
			:selectSnap="select_snap"
			@group-by="onGroupBy($event)"
			@status="status = $event"
			@date-range="date_range = $event"
			@dates="dates = $event"
			@view="onViewing($event)"
			@clear="onClear()"
			@search="onSearch($event)"
			@refresh="onRefresh()"
			@snap="onSnap($event)"
			@columns="onColumnsChange($event)"
			@media="onShowBooking($event)"
			@prod="onProduction($event)"
			@info="onInfo($event)"
			@tcp="onTcp($event)"
		/>
		<v-alert dense class="mx-3 mt-3" type="error" v-if="error_msg.length !== 0">
			{{ errorMessageFormat(error_msg) }}
		</v-alert>
		<div class="table-wrap" :style="`height: calc(100vh - ${dynamicHeight})`" v-if="!loading && !is_show_booking">
			<div class="table-wrap-container">
				<template v-if="is_empty_data || isFilterEmpty">
					<v-row class="mx-4 my-4">
						<div v-if="is_empty_data" class="no-media-text">No media bookings on this job.</div>
						<div v-if="isFilterEmpty" class="no-media-text">No media bookings.</div>
					</v-row>
				</template>
				<template v-if="!is_empty_data">
					<MediaPlanTable
						v-for="media of mediaList" 
						:key="media.id" 
						:color="media_color"
						:privileges="privileges"
						:jobId="joid" 
						:media="media"
						:groupBy="group_by"
						:statusSelect="status"
						:dateRange="date_range"
						:dates="dates"
						:dayOfWeek="day_of_week_num"
						:taskHeaders="table_header"
						:view="viewing"
						:snapId="select_snap"
						:kindParams="kind_params"
						:seachKey="search_key"
						@show-booking="onShowBooking($event)"
						@prod="onProduction($event)"
						@info="onInfo($event)"
						@tcp="onTcp($event)"
						@error="error_msg = $event"
						@close="onDialogClose($event)"
					/>
				</template>
			</div>
		</div>
		<MediaPlanTotalTable
			v-if="!loading && !is_show_booking "
			class="total-value-sticky"
			:totalValue="totalValue"
		/>		
		<MediaSnapSaveAsDialog 
			v-if="save_as_visible" 
			:visible="save_as_visible" 
			:title_text="'Save plan as'"
			:confirm_button="'Save as'"
			:icon="save_icon"
			:color="media_color"
			@cancel="save_as_visible = false; select_snap = (select_snap < 0 ? last_select_snap : select_snap)"
			@ok="onSnapSaveAs($event)"
		/>
		<MediaPlanBooking
			v-if="is_show_booking"
			:jobId="joid"
			:color="media_color"
			:privileges="privileges"
			:kindParams="kind_params"
			:mediaId="media_id"
			:supplierId="supplier_id"
			:bookingEdit="booking_edit"
			@close="onMediaBookingClose($event)"
		/>
		<ProductionDialog
			v-if="production_dialog_visible"
			:visible="production_dialog_visible" 
			:color="media_color"
			:jobId="joid"
			:kindParams="kind_params"
			:productionId="production_id"
			:productionEdit="production_edit"
			@close="onDialogClose($event)"
		/>
		<InfoDialog
			v-if="info_dialog_visible"
			:visible="info_dialog_visible" 
			:color="media_color"
			:jobId="joid"
			:snapId="select_snap"
			:infoId="info_id"
			@close="onDialogClose($event)"
		/>
		<CommissionDialog
			v-if="commission_dialog_visible"
			:visible="commission_dialog_visible"
			:maxWidth="dialog_width"
			:color="media_color"
			:jobId="joid"
			:snapId="select_snap"
			:commissionId="commission_id"
			:mediaPlanningData="media_planning_data"
			@close="onDialogClose($event)"
		/>
	</div>
</template>

<script>
import ProgressLinear from "@/components/progress/progress_getdata_linear.vue";
import MediaPlanHeader from "@/views/desktop/media/media_planning_header.vue";
import MediaPlanTable from "@/views/desktop/media/media_planning_table.vue";
import MediaPlanBooking from "@/views/desktop/media/media_planning_booking.vue";
import MediaPlanTotalTable from "@/views/desktop/media/media_planning_total_table.vue";
import MediaSnapSaveAsDialog from '@/views/desktop/media/media_snap_save_as_dialog.vue';
import ProductionDialog from "@/views/desktop/media/media_planning_production_dialog.vue";
import CommissionDialog from "@/views/desktop/media/media_planning_commission_dialog.vue";
import InfoDialog from "@/views/desktop/media/media_planning_info_dialog.vue";

import SaveIcon from "@/components/icons/save.vue";
import moment from 'moment';
import config from "@/config.js";

const Group_By_Items = [
	{ text: 'Media Owner', value: 'owner' },
	{ text: 'Media Outlet', value: 'outlet' },
	{ text: 'Kind', value: 'kind' },
	{ text: 'Phase', value: 'phase' },
];

const Status_Items = [
	{ text: 'Planned', key: 'planned', value: 1, range: [1, 6] },
	{ text: 'Booked', key: 'booked', value: 2, range: [2, 3] },
	{ text: 'Invoiced', key: 'invoiced', value: 4, range: [4, 5] },
	{ text: 'Reversed and Reversals', key: 'reverse', value: 100, range: [100, 101] },
];

const Date_Range_Week = 'week';
const Date_Range_Month = 'month';
const Date_Range_Next_Month = 'next_month';
const Date_Range_Last_Month = 'last_month';
const Date_Range_Range = 'range';

const Date_Range_Items = [
	{ text: 'All', value: 'all' },
	{ text: 'This Week', value: Date_Range_Week },
	{ text: 'This Month', value: Date_Range_Month },
	{ text: 'Next Month', value: Date_Range_Next_Month },
	{ text: 'Last Month', value: Date_Range_Last_Month },
	{ text: 'Selected Range', value: Date_Range_Range },
];

const DayOfWeek = [
	{
		day: 0,
		week: 'Sunday'
	},
	{
		day: 1,
		week: 'Monday'
	},
	{
		day: 2,
		week: 'Tuesday'
	},
	{
		day: 3,
		week: 'Wednesday'
	},
	{
		day: 4,
		week: 'thursday'
	},
	{
		day: 5,
		week: 'Friday'
	},
	{
		day: 6,
		week: 'Saturday'
	}
];

const Viewing_Items = [
	{ text: 'Schedule currency', value: 'schedule' },
	{ text: 'Division currency', value: 'division' }
];

const Table_Headers = [
	{
		text: ['Phase', 'Kind'],
		sortable: false,
		showable: true,
		divider: false,
		value: 'kind',
		width: 200
	},
	{
		text: ['Media Outlet', ''],
		sortable: false,
		showable: true,
		align: 'center',
		divider: false,
		value: 'outlet',
		width: 200
	},
	{
		text: ['Media Owner', ''],
		sortable: false,
		showable: true,
		align: 'center',
		divider: false,
		value: 'owner',
		width: 200
	},
	{
		text: ['Description', ''],
		sortable: false,
		showable: true,
		align: 'center',
		divider: false,
		value: 'description',
		width: 200
	},
	{
		text: ['Target audience', ''],
		sortable: false,
		showable: true,
		align: 'center',
		divider: false,
		value: 'target',
		width: 200
	},
	{
		text: ['Notes', ''],
		sortable: false,
		showable: true,
		align: 'center',
		divider: false,
		value: 'notes',
		width: 200
	},
	{
		text: ['Units', ''],
		sortable: false,
		showable: true,
		align: 'center',
		divider: false,
		value: 'units',
		width: 80
	},
	{
		text: ['Start date - End Date', ''],
		sortable: false,
		showable: true,
		align: 'center',
		divider: true,
		value: 'date',
		width: 200
	},
	{
		text: ['Supplier rate ($)', 'Supplier Amount ($)'],
		sortable: false,
		showable: true,
		align: 'center',
		divider: false,
		value: 'rate',
		width: 160
	},
	{
		text: ['Commission ($)', ''],
		sortable: false,
		showable: true,
		align: 'center',
		divider: false,
		value: 'commission',
		width: 160
	},
	{
		text: ['Net ($)', ''],
		sortable: false,
		showable: true,
		align: 'center',
		divider: false,
		value: 'net',
		width: 160
	},
	{
		text: ['Bill to client ($)', ''],
		sortable: false,
		showable: true,
		align: 'center',
		divider: false,
		value: 'bill',
		width: 160
	},
	{
		text: ['Margin ($)', 'Margin (%)'],
		sortable: false,
		showable: true,
		align: 'center',
		divider: false,
		value: 'margin',
		width: 160
	},
	{
		text: ['Revenue ($)', 'Revenue (%)'],
		sortable: false,
		showable: true,
		align: 'center',
		divider: false,
		value: 'revenue',
		width: 160
	},
	{
		text: ['3rd party commission (%)', ''],
		sortable: false,
		showable: true,
		align: 'center',
		divider: false,
		value: 'party',
		width: 180
	}
];

const Snap_Current = [
	{
		id: 0,
		desc: 'Current plan'
	},
	{
		id: -1,
		desc: 'Save plan as...'
	}
];

const SAVE_ICON = {
	name: SaveIcon,
	color: "white",
	width: 24, 
	height: 24,
};

const Break_Point_Width = 900;
const Link_Icon = '_';
const PLAN_TYPE = 'PLAN';

const Setting_Name = 'MediaPlan';

export default {
	name: 'MediaPlanner',
	data() {
		this.media_color = '#605AF6';
		this.group_by_items = Group_By_Items;
		this.status_items = Status_Items;
		this.date_range_items = Date_Range_Items;
		this.viewing_items = Viewing_Items;

		this.kind_params = {
			select_items: true,
			select_kinds: true,
			expense_kinds: true,
			overhead_mode: 0,
			billable_kinds: true,
		};
		
		/** default start from Sunday */
		this.day_of_week_num = DayOfWeek[0].day;

		return {
			loading: false,
			error_msg: '',
			joid: 0,
			dialog_width: 0,
			header_height: 60,
			user_id: 0,
			
			privileges: [],

			group_by: 'outlet',
			status: Status_Items.filter(status => { return status.key !== 'reverse'}),
			date_range: Date_Range_Items[0].value,
			viewing: Viewing_Items[0].value,
			dates: [],
			search_key: '',

			table_header: Table_Headers,
			column_header: Table_Headers,

			media_planning_data: [],
			snapshots: [],
			select_snap: 0,
			last_select_snap: 0,
			save_as_visible: false,
			save_icon: SAVE_ICON,
			is_empty_data: false,

			system_currency: null,
			supplier_items: [],

			media_id: 0,
			supplier_id: 0,
			booking_edit: true,
			is_show_booking: false,

			production_id: 0,
			production_edit: true,
			production_dialog_visible: false,

			commission_id: 0,
			commission_dialog_visible: false,

			info_id: 0,
			info_dialog_visible: false
		}
	},
	components: {
		ProgressLinear,
		MediaPlanHeader,
		MediaPlanTable,
		MediaPlanBooking,
		MediaPlanTotalTable,
		MediaSnapSaveAsDialog,
		ProductionDialog,
		CommissionDialog,
		InfoDialog
	},
	async created()
	{
		await this.getUserId();

		this.joid = this.$route.query.joid ? Number(this.$route.query.joid) : 0;

		if (this.$store.state.privileges && this.$store.state.privileges.length === 0) {
			await this.initPrivileges();
		} else {
			this.privileges = this.$store.state.privileges.filter(p => { return p.startsWith('Med') || p.startsWith('T')});
		}

		await this.getSettings();
		this.initDefaultValue();
		await this.getJobCurrency(this.joid);
		this.getListBookings(this.joid);
		this.getListSnapshotsForJob(this.joid);

		this.columnsFilter(this.group_by);
		this.dialog_width = this.$vuetify.breakpoint.width > Break_Point_Width ? Break_Point_Width : this.$vuetify.breakpoint.width - 8;

		const element =  document.getElementById("media-plan-header");
		this.header_height = element ? element.clientHeight : this.header_height;
	},
	beforeDestroy () {
		if (typeof window === 'undefined') return

		window.removeEventListener('resize', this.onResize, { passive: true })
	},
	mounted () {
		this.onResize();

		window.addEventListener('resize', this.onResize, { passive: true })
	},
	computed: {
		dynamicHeight() {
			return `${this.header_height + 84}px`
		},
		curWeek() {
			let week = [];
			
			const today = moment().weekday();
			if (today >= this.day_of_week_num) {
				const week_start = moment().startOf('week').day(this.day_of_week_num).format('YYYY-MM-DD');
				const week_end = moment().endOf('week').day(this.day_of_week_num + 6).format('YYYY-MM-DD');
				week = [week_start, week_end];
			}
			if (today < this.day_of_week_num) {
				const week_start = moment().startOf('week').day((this.day_of_week_num - 7) % 7).format('YYYY-MM-DD');
				const week_end = moment().endOf('week').day((this.day_of_week_num + 6) % 7).format('YYYY-MM-DD');
				week = [week_start, week_end];
			}

			return week;
		},
		curMonth() {
			let cur_month = [];

			const month_start = moment().startOf('month').format('YYYY-MM-DD');
			const month_end = moment().endOf('month').format('YYYY-MM-DD');
			cur_month = [month_start, month_end];

			return cur_month;
		},
		nextMonth() {
			let next_month = [];

			const next_month_date = moment().add(1, 'month');
			const month_start = next_month_date.clone().startOf('month').format('YYYY-MM-DD');
			const month_end = next_month_date.clone().endOf('month').format('YYYY-MM-DD');
			next_month = [month_start, month_end];

			return next_month;
		},
		lastMonth() {
			let last_month = [];

			const last_month_date = moment().subtract(1, 'month');
			const month_start = last_month_date.clone().startOf('month').format('YYYY-MM-DD');
			const month_end = last_month_date.clone().endOf('month').format('YYYY-MM-DD');
			last_month = [month_start, month_end];

			return last_month;
		},
		mediaList() {
			let media_list = [];

			if (this.media_planning_data.length !== 0) {
				if (this.group_by === Group_By_Items[0].value) {
					/** group by Media Owner */
					const media_group_by_owner = [];
					const exist_owner_codes = [];

					this.media_planning_data.forEach(media_planning => {
						if (media_planning.details && media_planning.details.length !== 0) {
							const phase = {
								id: media_planning.id,
								desc: media_planning.desc,
								order: media_planning.order
							};	
						
							media_planning.details.forEach(media => {
								if (media.supplier) {
									exist_owner_codes.push(media.supplier.owner_code);
								}

								media['phase'] = phase;
								media['job_id'] = this.joid;
								media['canEditPlan'] = media_planning.canEditPlan;
								media['currency'] = media_planning.currency;
								media_group_by_owner.push(media);
							})
						}
					});

					const unique_exist_owner_codes = [...new Set(exist_owner_codes)];
					unique_exist_owner_codes.sort();

					const media_group_by_owner_list = [];
					unique_exist_owner_codes.forEach((code, index) => {
						const filter_arr = media_group_by_owner.filter(m => { return m.supplier && m.supplier.owner_code === code });
						filter_arr.sort((a,b) => { return a.phase.order - b.phase.order });

						const media_obj = {
							id: code,
							desc: filter_arr[0].supplier.owner_name,
							order: index + 1,
							canEditPlan: true,
							details: filter_arr
						};
						media_group_by_owner_list.push(media_obj);
					});
					media_group_by_owner_list.sort((a, b) => {
						return (a.desc || '').localeCompare(b.desc || '');
					});
					media_group_by_owner_list.forEach((media, index) => {
						media.order = index + 1;
					});

					const other_arr = media_group_by_owner.filter(m => { return !m.supplier });
					if (other_arr.length !== 0) {
						const other_media_obj = {
							id: 'OTHER',
							desc: 'Other',
							order: media_group_by_owner_list.length + 1,
							canEditPlan: false,
							details: other_arr
						};
						media_group_by_owner_list.push(other_media_obj);
					}

					media_list = media_group_by_owner_list;
				}
				if (this.group_by === Group_By_Items[1].value) {
					/** group by Media Outlet */
					const media_group_by_supplier = [];
					const exist_supplier_ids = [];

					this.media_planning_data.forEach(media_planning => {
						if (media_planning.details && media_planning.details.length !== 0) {
							const phase = {
								id: media_planning.id,
								desc: media_planning.desc,
								order: media_planning.order
							};
						
							media_planning.details.forEach(media => {
								if (media.supplier) {
									exist_supplier_ids.push(media.supplier.id);
								}

								media['phase'] = phase;
								media['job_id'] = this.joid;
								media['canEditPlan'] = media_planning.canEditPlan;
								media['currency'] = media_planning.currency;
								media_group_by_supplier.push(media);
							})
						}
					});

					const unique_exist_supplier_ids = [...new Set(exist_supplier_ids)];
					unique_exist_supplier_ids.sort((a,b) => { return a - b });

					const media_group_by_supplier_list = [];
					unique_exist_supplier_ids.forEach((id, index) => {
						const filter_arr = media_group_by_supplier.filter(m => { return m.supplier && m.supplier.id === id });
						filter_arr.sort((a,b) => { return a.phase.order - b.phase.order });

						const media_obj = {
							id: id,
							desc: filter_arr[0].supplier.display_name,
							order: index + 1,
							canEditPlan: true,
							details: filter_arr
						};
						media_group_by_supplier_list.push(media_obj);
					});

					media_group_by_supplier_list.sort((a, b) => {
						return (a.desc || '').localeCompare(b.desc || '');
					});
					media_group_by_supplier_list.forEach((media, index) => {
						media.order = index + 1;
					});

					const other_arr = media_group_by_supplier.filter(m => { return !m.supplier });
					if (other_arr.length !== 0) {
						const other_media_obj = {
							id: 'OTHER',
							desc: 'Other',
							order: media_group_by_supplier_list.length + 1,
							canEditPlan: false,
							details: other_arr
						};
						media_group_by_supplier_list.push(other_media_obj);
					}
					
					media_list = media_group_by_supplier_list;
				}
				if (this.group_by === Group_By_Items[2].value) {
					/** group by Kind */
					const media_group_by_kind = [];
					const exist_kind_ids = [];

					this.media_planning_data.forEach(media_planning => {
						if (media_planning.details && media_planning.details.length !== 0) {
							const phase = {
								id: media_planning.id,
								desc: media_planning.desc,
								order: media_planning.order
							};	
						
							media_planning.details.forEach(media => {
								if (media.kind) {
									exist_kind_ids.push(media.kind.id);
								}

								media['phase'] = phase;
								media['job_id'] = this.joid;
								media['canEditPlan'] = media_planning.canEditPlan;
								media['currency'] = media_planning.currency;
								media_group_by_kind.push(media);
							})
						}
					});

					const unique_exist_kind_ids = [...new Set(exist_kind_ids)];
					unique_exist_kind_ids.sort((a,b) => { return a - b});

					const media_group_by_kind_list = [];
					unique_exist_kind_ids.forEach((id, index) => {
						const filter_arr = media_group_by_kind.filter(m => { return m.kind && m.kind.id === id });
						filter_arr.sort((a,b) => { return a.phase.order - b.phase.order });

						const media_obj = {
							id: id,
							desc: filter_arr[0].kind.name,
							order: index + 1,
							canEditPlan: true,
							details: filter_arr
						};
						media_group_by_kind_list.push(media_obj);
					});

					media_group_by_kind_list.sort((a, b) => {
						return (a.desc || '').localeCompare(b.desc || '');
					});
					media_group_by_kind_list.forEach((media, index) => {
						media.order = index + 1;
					});

					const other_arr = media_group_by_kind.filter(m => { return !m.kind });
					if (other_arr.length !== 0) {
						const other_media_obj = {
							id: 'OTHER',
							desc: 'Other',
							order: media_group_by_kind_list.length + 1,
							canEditPlan: false,
							details: other_arr
						};
						media_group_by_kind_list.push(other_media_obj);
					}
					
					media_list = media_group_by_kind_list;
				}
				if (this.group_by === Group_By_Items[3].value) {
					/** group by Phase */
					this.media_planning_data.forEach(media_planning => {
						if (media_planning.details && media_planning.details.length !== 0) {
							const phase = {
								id: media_planning.id,
								desc: media_planning.desc,
								order: media_planning.order
							};
						
							media_planning.details.forEach(media => {
								media['phase'] = phase;
								media['job_id'] = this.joid;
								media['currency'] = media_planning.currency;
							});
						}
					});
					media_list = this.media_planning_data;
					media_list.sort((a, b) => {
						return (a.desc || '').localeCompare(b.desc || '');
					});
					media_list.forEach((media, index) => {
						media.order = index + 1;
					});
				}
			}

			return media_list;
		},
		mediaListNew() {
			let media_list = [];

			if (this.media_planning_data.length !== 0) {
				/** status filter */
				let status_keys = [];
				if (this.status.length !== 0) {
					this.status.forEach(s => { status_keys = [...status_keys, ...s.range] });
				}

				/** date range filter */
				let date_range_key = [];
				if (this.date_range.length !== 0) {
					if (this.date_range === Date_Range_Week) {
						date_range_key = this.curWeek;
					} else if (this.date_range === Date_Range_Month) {
						date_range_key = this.curMonth;
					} else if (this.date_range === Date_Range_Next_Month) {
						date_range_key = this.nextMonth;
					} else if (this.date_range === Date_Range_Last_Month) {
						date_range_key = this.lastMonth;
					} else if (this.date_range === Date_Range_Range) {
						date_range_key = this.dates;
					}
				}

				let all_list = [];
				this.media_planning_data.forEach(data => {
					const details = data.details ? data.details : [];
					details.forEach(detail => {
						detail['currency'] = data.currency;
						detail['phase'] = {
							id: data.id,
							desc: data.desc
						};
					});
					all_list = [...all_list, ...details];
				});

				const plan_list = all_list.filter(list => { return list.record_type === 'PLAN' });
				const other_list = all_list.filter(list => { return list.record_type !== 'PLAN' });

				plan_list.forEach(list => {
					/**
					 * Filter by booking_start only, including both date-range boundaries.
					 * Example: a 29 Jul–3 Aug booking appears in July, but not in August.
					 */
					const bookings = list.bookings.filter(book => {
						const start_date = moment(book.booking_start, 'YYYYMMDD').format('YYYY-MM-DD');
						const is_fall_range = date_range_key.length === 0 ? true : this.isFallRange(start_date, date_range_key);

						return status_keys.includes(book.status) && is_fall_range;
					});

					list['bookings_filter'] = bookings;
				});

				media_list = [...plan_list, ...other_list];
				
				if (this.group_by === Group_By_Items[0].value) {
					/** group by Media Owner */
					media_list.forEach(media => {
						media['group'] = media.supplier ? media.supplier.owner_code : 'Other';
					});
				}
				if (this.group_by === Group_By_Items[1].value) {
					/** group by Media Outlet */
					media_list.forEach(media => {
						media['group'] = media.supplier ? media.supplier.code : 'Other';
					});
				}
				if (this.group_by === Group_By_Items[2].value) {
					/** group by Kind */
					media_list.forEach(media => {
						media['group'] = media.kind ? media.kind.code : 'Other';
					});
				}
				if (this.group_by === Group_By_Items[3].value) {
					/** group by Phase */
					media_list.forEach(media => {
						media['group'] = media.phase ? media.phase.desc : 'Other';
					});
				}
			}

			return media_list;
		},
		normalisedSearchKey() {
			return this.search_key && this.search_key.length !== 0 ? this.search_key.toString().trim().toLowerCase() : '';
		},
		totalValue() {
			const total_value_item = {
				id: 0,
				rate: `${this.system_currency && this.system_currency.code}${Link_Icon}0.00`,
				commission: `${this.system_currency && this.system_currency.code}${Link_Icon}0.00`,
				net: `${this.system_currency && this.system_currency.code}${Link_Icon}0.00`,
				bill: `${this.system_currency && this.system_currency.code}${Link_Icon}0.00`,
				revenue_perc: `${Link_Icon}0.00%`,
				revenue: `${this.system_currency && this.system_currency.code}${Link_Icon}0.00`,
				party: `${this.system_currency && this.system_currency.code}${Link_Icon}0.00`,
			};

			if (this.media_planning_data.length !== 0) {
				let all_list = [];
				this.media_planning_data.forEach(data => {
					const details = data.details ? data.details : [];
					all_list = [...all_list, ...details];
				});

				const normalised_search_key = this.normalisedSearchKey;
				if (normalised_search_key.length !== 0) {
					const search_fields = ['description', 'outlet', 'owner', 'target', 'notes'];
					all_list = all_list.filter(list => {
						return search_fields.some(field => {
							let search_value = '';

							if (field === 'description') {
								search_value = this.getMediaSearchDescription(list);
							}
							if (field === 'outlet' || field === 'owner') {
								search_value = list.supplier;
							}
							if (field === 'target') {
								search_value = list.target;
							}
							if (field === 'notes') {
								search_value = list.notes;
							}

							return this.getPreSearchArr(search_value).some(text => { return text.toLowerCase().includes(normalised_search_key) });
						});
					});
				}

				if (all_list.length !== 0) {
					let plan_booking_list = [];
					all_list.filter(list => {
						if (list.record_type === 'PLAN') {
							plan_booking_list = [...plan_booking_list, ...list.bookings];
						}
					});
					plan_booking_list = plan_booking_list.filter(list => { return list.status < 100 });

					let prod_list = [];
					all_list.filter(list => {
						if (list.record_type === 'PROD') {
							prod_list = [...prod_list, list];
						}
					});
					
					let tpc_list = [];
					all_list.filter(list => {
						if (list.record_type === 'TPC') {
							let bookings = [];

							if (list.details && list.details.length !== 0) {
								list.details.forEach(detail => {
									bookings = [
										...bookings,
										...(Array.isArray(detail.bookings) ? detail.bookings : [])
									];
								});
							}

							list['bookings'] = bookings;
							tpc_list = [...tpc_list, list];
						}
					});
					
				
					/** status filter */
					let status_keys = [];
					if (this.status.length !== 0) {
						this.status.forEach(s => { status_keys = [...status_keys, ...s.range] });
					}

					plan_booking_list = plan_booking_list.filter(book => { return status_keys.includes(book.status) });
					prod_list = prod_list.filter(prod => { return status_keys.includes(prod.status) });
					tpc_list.forEach(tpc => {
						const bookings = JSON.parse(JSON.stringify(tpc.bookings));
						tpc.bookings = bookings.filter(booking => { return status_keys.includes(booking.booking_status) });
					})


					/** date range filter */
					let date_range_key = [];
					if (this.date_range === Date_Range_Week) {
						date_range_key = this.curWeek;
					} else if (this.date_range === Date_Range_Month) {
						date_range_key = this.curMonth;
					} else if (this.date_range === Date_Range_Next_Month) {
						date_range_key = this.nextMonth;
					} else if (this.date_range === Date_Range_Last_Month) {
						date_range_key = this.lastMonth;
					} else if (this.date_range === Date_Range_Range) {
						date_range_key = this.dates;
					}

					if (date_range_key.length !== 0) {
						/**
						 * Filter by booking_start only, including both date-range boundaries.
						 * Example: a 29 Jul–3 Aug booking appears in July, but not in August.
						 */
						plan_booking_list = plan_booking_list.filter(book => {
							const start_date = moment(book.booking_start, 'YYYYMMDD').format('YYYY-MM-DD');
							if (this.isFallRange(start_date, date_range_key)) {
								return book;
							}
						});

						prod_list = prod_list.filter(prod => {
							const start_date = moment(prod.prod_start, 'YYYYMMDD').format('YYYY-MM-DD');
							if (this.isFallRange(start_date, date_range_key)) {
								return prod;
							} 
						});

						tpc_list.forEach(tpc => {
							const bookings = JSON.parse(JSON.stringify(tpc.bookings));

							/**
							 * Filter by booking_start only, including both date-range boundaries.
							 * Example: a 29 Jul–3 Aug booking appears in July, but not in August.
							 */
							const bookings_filter = bookings.filter(booking => {
								const start_date = moment(booking.booking_start, ['YYYYMMDD', 'YYYY-MM-DD']).format('YYYY-MM-DD');
								if (this.isFallRange(start_date, date_range_key)) {
									return booking;
								}
							}); 

							tpc.bookings = bookings_filter;
						});
					}					
				
					/** schedule */
					if (this.viewing === Viewing_Items[0].value) {
						const available_list = all_list.filter(list => { return list.record_type === 'PLAN' });
						const is_same_cost_currency = available_list.every(list => { return list.cost_currency.id === available_list[0].cost_currency.id });
						if (is_same_cost_currency) {
							if (plan_booking_list.length !== 0) {
								/** Supplier Amount -> gross */
								const gross_fc_all = plan_booking_list.map(list => { return list.amount_fc });
								const gross_fc_total = gross_fc_all.reduce((accumulator, currentValue) => accumulator + currentValue);
								const formatted_gross_fc = new Intl.NumberFormat('en-US', {
									minimumFractionDigits: 2,
									maximumFractionDigits: 2
								}).format(gross_fc_total);
								total_value_item['rate'] = `${available_list[0].cost_currency.code}${Link_Icon}${formatted_gross_fc}`;

								/** Commission -> commission */
								const commission_fc_all = plan_booking_list.map(list => { return list.commamount_fc });
								const commission_fc_total = commission_fc_all.reduce((accumulator, currentValue) => accumulator + currentValue);
								const formatted_commission_fc = new Intl.NumberFormat('en-US', {
									minimumFractionDigits: 2,
									maximumFractionDigits: 2
								}).format(commission_fc_total);
								total_value_item['commission'] = `${available_list[0].cost_currency.code}${Link_Icon}${formatted_commission_fc}`;
							} else {
								total_value_item['rate'] = `${this.system_currency && this.system_currency.code}${Link_Icon}0.00`;
								total_value_item['commission'] = `${this.system_currency && this.system_currency.code}${Link_Icon}0.00`;
							}
						} else {
							total_value_item['rate'] = `${Link_Icon}`;
							total_value_item['commission'] = `${Link_Icon}`;
						}

						
						const is_same_value_currency = available_list.every(list => { return list.value_currency.id === available_list[0].value_currency.id });
						if (is_same_value_currency) {
							if (plan_booking_list.length !== 0) {
								/** Net -> netamount */
								const net_fc_all = plan_booking_list.map(list => { return list.net_fc });
								const net_fc_total = net_fc_all.reduce((accumulator, currentValue) => accumulator + currentValue);
								const formatted_net_fc = new Intl.NumberFormat('en-US', {
									minimumFractionDigits: 2,
									maximumFractionDigits: 2
								}).format(net_fc_total);
								total_value_item['net'] = `${available_list[0].value_currency.code}${Link_Icon}${formatted_net_fc}`;

								/** Revenue -> revenue */
								const revenue_fc_all = plan_booking_list.map(list => { return list.revenue_fc });
								const revenue_fc_total = revenue_fc_all.reduce((accumulator, currentValue) => accumulator + currentValue);
								const formatted_revenue_fc = new Intl.NumberFormat('en-US', {
									minimumFractionDigits: 2,
									maximumFractionDigits: 2
								}).format(revenue_fc_total);
								total_value_item['revenue'] = `${available_list[0].value_currency.code}${Link_Icon}${formatted_revenue_fc}`;
							} else {
								total_value_item['net'] = `${this.system_currency && this.system_currency.code}${Link_Icon}0.00`;
								total_value_item['revenue'] = `${this.system_currency && this.system_currency.code}${Link_Icon}0.00`;
							}
						} else {
							total_value_item['net'] = `${Link_Icon}`;
							total_value_item['revenue'] = `${Link_Icon}`;
						}
						
						
						const available_bill_list = all_list.filter(list => { return list.record_type === 'PLAN' || list.record_type === 'PROD' });
						const available_bill_currency_ids = available_bill_list.map(list => { return list.value_currency ? list.value_currency.id : list.currency.id });
						const is_same_value_currency_bill = available_bill_currency_ids.every(id => { return id === available_bill_currency_ids[0] });
						if (is_same_value_currency_bill) {
							const bill_list = [...plan_booking_list, ...prod_list];
							if (bill_list.length !== 0) {
								/** Bill -> value */
								const bill_fc_all = bill_list.map(list => {
									if (list.record_type === 'PROD') {
										return list.amount_div;
									} else {
										return list.value_fc;
									}
								});
								const bill_fc_total = bill_fc_all.reduce((accumulator, currentValue) => accumulator + currentValue);
								const formatted_bill_fc = new Intl.NumberFormat('en-US', {
									minimumFractionDigits: 2,
									maximumFractionDigits: 2
								}).format(bill_fc_total);
								const currency_code = available_bill_list[0].value_currency ? available_bill_list[0].value_currency.code : available_bill_list[0].currency.code;
								total_value_item['bill'] = `${currency_code}${Link_Icon}${formatted_bill_fc}`;

								/** Revenue % -> revenue_perc */
								const net_fc_all = plan_booking_list.map(list => { return list.net_fc });
								const net_fc_total = net_fc_all.length === 0 ? 0 : net_fc_all.reduce((accumulator, currentValue) => accumulator + currentValue);
								const revenue_perc = bill_fc_total === 0 ? 0 : ((bill_fc_total - net_fc_total) / bill_fc_total) * 100;
								total_value_item['revenue_perc'] = `${Link_Icon}${Number(revenue_perc).toFixed(2)}%`;

							} else {
								const currency = available_bill_list[0] ? (available_bill_list[0].value_currency ? available_bill_list[0].value_currency : available_bill_list[0].currency) : null;
								total_value_item['bill'] = `${currency ? currency.code : this.system_currency.code }${Link_Icon}0.00`;
								total_value_item['revenue_perc'] = `${Link_Icon}0.00%`;
							}
						} else {
							total_value_item['bill'] = `${Link_Icon}`;
							total_value_item['revenue_perc'] = `${Link_Icon}`;
						}

						/** TPC commission -> tpc_amount */
						const is_same_tpc_currency = tpc_list.every(list => { return list.tpc_currency.id === tpc_list[0].tpc_currency.id });
						if (is_same_tpc_currency) {
							if (tpc_list.length !== 0) {
								const tpc_party = [];
								tpc_list.forEach(list => {
									const tpc_commperc = list.tpc_commperc;
									const gross_value_arr = list.bookings.map(booking => { return Number((booking.booking_gross_fc * booking.tpc_booking_exrate_fc).toFixed(2)) });
									const gross_total_amount = gross_value_arr.length === 0 ? 0 : gross_value_arr.reduce((accumulator, currentValue) => accumulator + currentValue);
									const gross_amount = gross_total_amount * tpc_commperc / 100;
									const fixed_gross_amount = gross_amount.toFixed(2);
									
									tpc_party.push(Number(fixed_gross_amount));
								});

								const party_total_amount = tpc_party.length === 0 ? 0 : tpc_party.reduce((accumulator, currentValue) => accumulator + currentValue);
								const formatted_tcp_fc = new Intl.NumberFormat('en-US', {
									minimumFractionDigits: 2,
									maximumFractionDigits: 2
								}).format(party_total_amount);
								total_value_item['party'] = `${tpc_list[0].tpc_currency.code}${Link_Icon}${formatted_tcp_fc}`;
							} else {
								total_value_item['party'] = `${this.system_currency && this.system_currency.code}${Link_Icon}0.00`;
							}
						} else {
							total_value_item['party'] = `${Link_Icon}`;
						}					
					}

					/** division */
					if (this.viewing === Viewing_Items[1].value) {
						if (plan_booking_list.length !== 0) {
							/** Supplier Amount -> gross */
							const gross_div_all = plan_booking_list.map(list => { return list.amount_div });
							const gross_div_total = gross_div_all.length === 0 ? 0 : gross_div_all.reduce((accumulator, currentValue) => accumulator + currentValue);
							const formatted_gross_div = new Intl.NumberFormat('en-US', {
								minimumFractionDigits: 2,
								maximumFractionDigits: 2
							}).format(gross_div_total);
							total_value_item['rate'] = `${this.system_currency.code}${Link_Icon}${formatted_gross_div}`;

							/** Commission -> commission */
							const commission_div_all = plan_booking_list.map(list => { return list.commamount_div });
							const commission_div_total = commission_div_all.length === 0 ? 0 : commission_div_all.reduce((accumulator, currentValue) => accumulator + currentValue);
							const formatted_commission_div = new Intl.NumberFormat('en-US', {
								minimumFractionDigits: 2,
								maximumFractionDigits: 2
							}).format(commission_div_total);
							total_value_item['commission'] = `${this.system_currency.code}${Link_Icon}${formatted_commission_div}`;

							/** Net -> netamount */
							const net_div_all = plan_booking_list.map(list => { return list.net_div });
							const net_div_total = net_div_all.length === 0 ? 0 : net_div_all.reduce((accumulator, currentValue) => accumulator + currentValue);
							const formatted_net_div = new Intl.NumberFormat('en-US', {
								minimumFractionDigits: 2,
								maximumFractionDigits: 2
							}).format(net_div_total);
							total_value_item['net'] = `${this.system_currency.code}${Link_Icon}${formatted_net_div}`;

							/** Revenue -> revenue */
							const revenue_div_all = plan_booking_list.map(list => { return list.revenue_div });
							const revenue_div_total = revenue_div_all.length === 0 ? 0 : revenue_div_all.reduce((accumulator, currentValue) => accumulator + currentValue);
							const formatted_revenue_div = new Intl.NumberFormat('en-US', {
								minimumFractionDigits: 2,
								maximumFractionDigits: 2
							}).format(revenue_div_total);
							total_value_item['revenue'] = `${this.system_currency.code}${Link_Icon}${formatted_revenue_div}`;
						} else {
							total_value_item['rate'] = `${this.system_currency.code}${Link_Icon}0.00`;
							total_value_item['commission'] = `${this.system_currency.code}${Link_Icon}0.00`;
							total_value_item['net'] = `${this.system_currency.code}${Link_Icon}0.00`;
							total_value_item['revenue'] = `${this.system_currency.code}${Link_Icon}0.00`;
						}
						
						
						/** TPC commission -> tpc_amount */
						if (tpc_list.length !== 0) {
							const tpc_party = [];
							tpc_list.forEach(list => {
								const tpc_commperc = list.tpc_commperc;
								const gross_value_arr = list.bookings.map(booking => { return Number((booking.booking_gross_fc * booking.tpc_booking_exrate_div).toFixed(2)) });
								const gross_total_amount = gross_value_arr.length === 0 ? 0 : gross_value_arr.reduce((accumulator, currentValue) => accumulator + currentValue);
								const gross_amount = gross_total_amount * tpc_commperc / 100;
								const fixed_gross_amount = gross_amount.toFixed(2);
									
								tpc_party.push(Number(fixed_gross_amount));
							});

							const party_total_amount = tpc_party.length === 0 ? 0 : tpc_party.reduce((accumulator, currentValue) => accumulator + currentValue);
							const formatted_tcp_fc = new Intl.NumberFormat('en-US', {
								minimumFractionDigits: 2,
								maximumFractionDigits: 2
							}).format(party_total_amount);
							total_value_item['party'] = `${this.system_currency.code}${Link_Icon}${formatted_tcp_fc}`;
						} else {
							total_value_item['party'] = `${this.system_currency.code}${Link_Icon}0.00`;
						}

						/** Bill -> value */
						const available_bill_list = [...plan_booking_list, ...prod_list];
						if (available_bill_list.length !== 0) {
							const bill_div_all = available_bill_list.map(list => {
								if (list.record_type === 'PROD') {
									return list.amount_div;
								} else {
									return list.value_div;
								}
							});
							const bill_div_total = bill_div_all.length === 0 ? 0 : bill_div_all.reduce((accumulator, currentValue) => accumulator + currentValue);
							const formatted_bill_div = new Intl.NumberFormat('en-US', {
								minimumFractionDigits: 2,
								maximumFractionDigits: 2
							}).format(bill_div_total);
							total_value_item['bill'] = `${this.system_currency.code}${Link_Icon}${formatted_bill_div}`;

							/** Revenue % -> revenue_perc */
							let net_div_total = (total_value_item['net'].split('_'))[1];
							if (net_div_total.includes(',')) {
								const numberString = net_div_total.replace(/[^0-9.-]+/g, "");
								net_div_total = Number(numberString).toFixed(2);
							}
							const revenue_perc = bill_div_total === 0 ? 0 : ((bill_div_total - Number(net_div_total)) / bill_div_total) * 100;
							total_value_item['revenue_perc'] = `${Link_Icon}${Number(revenue_perc).toFixed(2)}%`;
						} else {
							total_value_item['bill'] = `${this.system_currency.code}${Link_Icon}0.00`;
							total_value_item['revenue_perc'] = `${Link_Icon}0.00%`
						}
					}
				}
			}

			return total_value_item;
		},
		isFilterEmpty() {
			let is_filter_empty = false;

			if (this.media_planning_data.length !== 0 && this.normalisedSearchKey.length !== 0) {
				let all_list = [];
				
				this.media_planning_data.forEach(data => {
					const details = data.details ? data.details : [];
					all_list = [...all_list, ...details];
				});

				const normalised_search_key = this.normalisedSearchKey;
				if (normalised_search_key.length !== 0) {
					const search_fields = ['description', 'outlet', 'owner', 'target', 'notes'];
					all_list = all_list.filter(list => {
						return search_fields.some(field => {
							let search_value = '';

							if (field === 'description') {
								search_value = this.getMediaSearchDescription(list);
							}
							if (field === 'outlet' || field === 'owner') {
								search_value = list.supplier;
							}
							if (field === 'target') {
								search_value = list.target;
							}
							if (field === 'notes') {
								search_value = list.notes;
							}

							return this.getPreSearchArr(search_value).some(text => { return text.toLowerCase().includes(normalised_search_key) });
						});
					});
				}

				is_filter_empty = all_list.length === 0 ? true : false;
			}

			return is_filter_empty
		}
	},
	methods: {
		getPreSearchArr(value) {
			const value_arr = Array.isArray(value) ? value : [value];
			return value_arr.map(item => {
				if (item === null || item === undefined) {
					return '';
				}
				if (typeof(item) === 'object') {
					return item.display_name || item.owner_name || item.name || item.desc || item.adbShort || '';
				}
				return item.toString();
			}).filter(text => { return text.length !== 0 });
		},
		getTotalValuePreSearchArr(media) {
			return [
				...this.getPreSearchArr(this.getMediaSearchDescription(media)),
				...this.getPreSearchArr(media.supplier),
				...this.getPreSearchArr(media.supplier),
				...this.getPreSearchArr(media.target),
				...this.getPreSearchArr(media.notes)
			];
		},
		getMediaSearchDescription(media) {
			if (media.record_type === 'INFO') {
				return media.info_text;
			}
			if (media.record_type === 'TPC') {
				const apply_to_str = ['Gross', 'Net', 'Bill to client'];
				const supplier = media.tpc_supplier ? media.tpc_supplier.adbShort : '';
				return `Third party commission for supplier: ${supplier}, comm%: ${media.tpc_commperc}, apply over: ${apply_to_str[media.tpc_applyto]}`;
			}
			return media.description;
		},
		onResize () {
			const element = document.getElementById("media-plan-header");
			this.header_height = element ? element.clientHeight : this.header_height;
		},
		async getUserId() {
			this.loading = true;

			const url = '/api/user/me';

			await axios
				.get(url, {
					headers: {
						"Authorization": "Bearer " + this.$store.state.token,
						"Accept": "application/json"
					}
				})
				.then((val) => {
					const { status, data, reason } = val.data;

					if (status == "OK") {
						this.user_id = data.id;
					} else {
						this.error_msg = reason;
					}
				})
				.catch((e) => {
					this.error_msg = e.message;
				})
				.finally(() => {
					this.loading = false;
				});
		},
		async initPrivileges() {
			this.loading = true;

			const url = "/api/user/me/privileges";
			
			await axios
				.get(url, {
					headers: {
						"Authorization": "Bearer " + this.$store.state.token,
						"Accept": "application/json"
					}					
				})
				.then(val => {
					const {status, data}  = val.data;

					if (status === "OK") {
						this.privileges = data.filter(p => { return p.startsWith('Med') || p.startsWith('T') });
					}
				})
				.catch((e) => {
					console.log(e.message);
				})
				.finally(() => {
					this.loading = false;
				});
		},
		async getSettings() {
			this.loading = true;

			const usid = this.user_id;
			const winname = config.win_name[`${Setting_Name}`];

			const url = `/api/user/${usid}/webwinsetup/${winname}`;

			await axios
				.get(url, {
					headers: {
						"Authorization": "Bearer " + this.$store.state.token,
						"Accept": "application/json"
					}
				})
				.then((val) => {
					const { status, data, reason } = val.data;

					if (status == "OK") {
						if (data.length !== 0) {							
							const current_settings = data.filter(d => { return d.user_id === usid });
							const settings_winname = current_settings[0].window_name;
							const settings_windata_json = current_settings[0].windata_json;
							if (settings_winname === winname) {
								const windata = JSON.parse(settings_windata_json);

								const group = windata.group;
								if (group) {
									this.group_by = group;
								}

								const viewing = windata.viewing;
								if (viewing) {
									this.viewing = viewing;
								}

								const column = windata.column;
								if (column) {
									let columns_orignal = [];

									if (this.group_by === Group_By_Items[0].value || this.group_by === Group_By_Items[1].value) {
										const column_filter = Table_Headers.filter(header => { return header.value !== this.group_by });
										columns_orignal = JSON.parse(JSON.stringify(column_filter));
									} else {
										columns_orignal = JSON.parse(JSON.stringify(Table_Headers));
									}

									const select_columns_view = [];
									columns_orignal.forEach(c => {
										if (column.includes(c.value)) {
											c.showable = true;
											select_columns_view.push(c);
										} else {
											c.showable = false;
											select_columns_view.push(c);
										}
									});

									this.table_header = select_columns_view.filter(head => { return head.showable === true });
									this.column_header = select_columns_view;
								}
							}
						}
					} else {
						this.error_msg = reason;
					}
				})
				.catch((e) => {
					this.error_msg = e.message;
				})
				.finally(() => {
					this.loading = false;
				});
		},
		updateSettings(win_setup) {
			const usid = this.user_id;
			const winname = config.win_name[`${Setting_Name}`];

			const url = `/api/user/${usid}/webwinsetup/${winname}`;

			const params_obj = {};
			params_obj['value'] = win_setup;

			return new Promise((resolve, reject) => {
				axios
					.post(url, params_obj, {
						headers: {
							"Authorization": "Bearer " + this.$store.state.token,
							"Accept": "application/json"
						}
					})
					.then((val) => {
						const json = val.data;
						const { status, data, reason } = json;

						if (status == "OK") {
							resolve(data);
						} else {
							reject(reason);
						}
					})
					.catch((e) => {
						reject(e.message);
					});
			});
		},
		initDefaultValue() {
			this.status = Status_Items.filter(status => { return status.key !== 'reverse'});
			this.date_range = Date_Range_Items[0].value;
			this.select_snap = 0;
		},
		async getJobCurrency(job_id) {
			this.loading = true;

			const url = `/api/jobs/${job_id}`;

			await axios
				.get(url, {
					headers: {
						"Authorization": "Bearer " + this.$store.state.token,
						"Accept": "application/json"
					}
				})
				.then((val) => {
					const {status, data, reason } = val.data;
					if (status == "OK") {
						this.system_currency = data.currency;
					} else {
						this.error_msg = reason;
					}
				})
				.catch(e => {
					this.error_msg = e.message;
				})
				.finally(() => {
					this.loading = false;
				});
		},
		async getListBookings(job_id, snap_id=null) {
			this.loading = true;

			const params = [];
			params.push("job_id=" + job_id);
			if (snap_id !== null) {
				params.push("snap_id=" + snap_id);
			}

			let url = "/api/media/media_planning";
			if (params.length > 0) {
				url+= "?" + params.join('&');
			}

			await axios
				.get(url, {
					headers: {
						"Authorization": "Bearer " + this.$store.state.token,
						"Accept": "application/json"
					}
				})
				.then((val) => {
					const {status, data, reason } = val.data;
					if (status == "OK") {
						this.media_planning_data = data[0].phases ? data[0].phases : [];

						let all_list = [];
						this.media_planning_data.forEach(data => {
							const details = data.details ? data.details : [];
							details.forEach(detail => {
								if (detail.record_type === PLAN_TYPE && !detail.bookings) {
									detail.bookings = [];
								}
							})
							all_list = [...all_list, ...details];
						});

						this.is_empty_data = all_list.length === 0 ? true : false;

					} else {
						this.error_msg = reason;
					}
				})
				.catch(e => {
					this.error_msg = e.message;
				})
				.finally(() => {
					this.loading = false;
				});
		},
		getListSnapshotsForJob(job_id, is_create_snap=false) {
			const url = `/api/media/media_planning/${job_id}/snapshots`;

			axios
				.get(url, {
					headers: {
						"Authorization": "Bearer " + this.$store.state.token,
						"Accept": "application/json"
					}
				})
				.then((val) => {
					const {status, data, reason } = val.data;
					if (status == "OK") {
						if (is_create_snap) {
							const exisr_ids =	this.snapshots.map(snap => { return snap.id });
							const new_snap = data.filter(d => { return !exisr_ids.includes(d.id) });
							this.select_snap = new_snap[0].id;
						} 
						this.snapshots = [...Snap_Current, ...data];
						
					} else {
						this.error_msg = reason;
					}
				})
				.catch(e => {
					this.error_msg = e.message;
				});
		},
		onGroupBy(group) {
			this.group_by = group;
			this.columnsFilter(group);

			const win_setup_obj = {
				group: this.group_by,
				viewing: this.viewing,
				column: this.column_header.filter(head => { return head.showable === true }).map(head => { return head.value })
			};
			const win_setup = JSON.stringify(win_setup_obj);

			this.updateSettings(win_setup).catch((e) => {
				this.error_msg = e;
			});
		},
		onViewing(viewing) {
			this.viewing = viewing;

			const win_setup_obj = {
				group: this.group_by,
				viewing: this.viewing,
				column: this.column_header.filter(head => { return head.showable === true }).map(head => { return head.value })
			};
			const win_setup = JSON.stringify(win_setup_obj);

			this.updateSettings(win_setup).catch((e) => {
				this.error_msg = e;
			});
		},
		onClear() {
			this.search_key = '';
		},
		onSearch(search_text) {
			this.search_key = search_text || '';
		},
		async onRefresh() {
			await this.getSettings();
			this.initDefaultValue();
			this.getListBookings(this.joid);
		},
		onSnap(id) {
			this.last_select_snap = this.select_snap;
			this.select_snap = id;
			
			if (id < 0) {
				this.save_as_visible = true;
			}
			if (id === 0) {
				this.getListBookings(this.joid);
			}
			if (id > 0) {
				this.getListBookings(this.joid, id);
			}
		},
		onSnapSaveAs(name) {
			this.save_as_visible = false;

			const url = "/api/media/media_planning/snapshot";
			const params_obj = {
				job_id: this.joid,
				desc: name
			};
			
			axios
				.post(url, params_obj, {
					headers: {
						"Authorization": "Bearer " + this.$store.state.token,
						"Accept": "application/json"
					}
				})
				.then((val) => {
					const { status, data, reason } = val.data;

					if (status == "OK") {
						this.media_planning_data = [];
						this.media_planning_data = data[0].phases ? data[0].phases : [];
						this.getListSnapshotsForJob(this.joid, true)
					} else {
						this.error_msg = reason;
					}
				})
				.catch((e) => {
					this.error_msg = e.message;
				});
		},
		columnsFilter(group_by) {
			/** owner, outlet */
			let columns_orignal = [];

			if (group_by === Group_By_Items[0].value || group_by === Group_By_Items[1].value) {
				const column_filter = Table_Headers.filter(header => { return header.value !== this.group_by });
				columns_orignal = JSON.parse(JSON.stringify(column_filter));
			} else {
				columns_orignal = JSON.parse(JSON.stringify(Table_Headers));
			}

			const select_columns_view = [];
			const column_header_keys = this.column_header.filter(header => { return header.showable === true }).map(header => { return header.value });
			columns_orignal.forEach(c => {
				if (column_header_keys.includes(c.value)) {
					c.showable = true;
					select_columns_view.push(c);
				} else {
					c.showable = false;
					select_columns_view.push(c);
				}
			});

			this.column_header = select_columns_view;
			this.table_header = this.column_header.filter(head => { return head.showable === true });
		},
		onColumnsChange(column) {
			this.table_header = column.filter(head => { return head.showable === true });

			const win_setup_obj = {
				group: this.group_by,
				viewing: this.viewing,
				column: column.filter(head => { return head.showable === true }).map(head => { return head.value })
			};
			const win_setup = JSON.stringify(win_setup_obj);

			this.updateSettings(win_setup).catch((e) => {
				this.error_msg = e;
			});
		},
		onShowBooking(event) {
			if (typeof(event) === 'object' && Number.isNaN(event) === false) {
				this.media_id = event.media;
				this.supplier_id = event.supplier;
				this.booking_edit = event.edit;
			} else {
				this.media_id =0;
				this.supplier_id = 0;
				this.booking_edit = true;
			}
			this.is_show_booking = true;
			
			window.open("/media/disable");
		},
		onProduction(prod) {
			this.production_id = prod.id;
			this.production_edit = prod.edit;
			this.production_dialog_visible = true;
		},
		onInfo(info_id) {
			this.info_id = info_id;
			this.info_dialog_visible = true;
		},
		onTcp(commission_id) {
			this.commission_id = commission_id;
			this.commission_dialog_visible = true;
		},
		onMediaBookingClose(is_refresh) {
			this.is_show_booking = false;

			window.open('/media/enable');
			
			if (is_refresh) {
				this.getListBookings(this.joid);
			}
		},
		onDialogClose(is_refresh) {
			this.production_dialog_visible = false;
			this.info_dialog_visible = false;
			this.commission_dialog_visible = false;

			if (is_refresh) {
				this.getListBookings(this.joid);
			}
		},
		isFallRange(start_date, date_range) {
			if (date_range.length === 0) {
				return true;
			}

			return moment(start_date).isBetween(date_range[0], date_range[1], undefined, '[]');
		},
		errorMessageFormat(msg) {
			if (msg.length !== 0) {
				this.tid = setTimeout(this._removeMsg, 5000);

				if (msg.includes(config.server_msge_flag)) {
					const msg_arr = msg.split(config.server_msge_flag);
					return msg_arr[1];
				} else {
					return msg;
				}				
			}
		},
		_removeMsg() {
			this.error_msg = '';
		},
	}
}
</script>

<style lang="scss" scoped>
html { 
	overflow-y: hidden !important;
}
.total-value-sticky {
	position: sticky;
	bottom: 0px;
}
.table-wrap {
	overflow: auto;
}
.table-wrap-container {
  height: 99%;
}
.no-media-text {
	font-family: Roboto;
	font-weight: 400;
	font-size: 16px;
	line-height: 24px;
	letter-spacing: 0.5px;
	color: #000000;
}
</style>
